import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import type { ClinicUser, UserRole } from '../domain/types';
import { createDemoData } from '../data/demoData';
import { getFirebaseServices, isDemoMode } from '../firebase/client';
import { toPortugueseError } from '../firebase/errors';
import { tokens } from '../theme/tokens';

interface AuthContextValue {
  user: ClinicUser | null;
  loading: boolean;
  demoMode: boolean;
  sessionError: string;
  signIn: (email: string, password: string, preferredRole?: UserRole) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const demoSessionKey = 'clinic-demo-session-role';

function demoUser(role: UserRole): ClinicUser {
  const user = createDemoData().users.find((candidate) => candidate.role === role);
  if (!user) throw new Error('Demo profile is unavailable.');
  return user;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const demoMode = isDemoMode();
  const [user, setUser] = useState<ClinicUser | null>(() => {
    if (!demoMode) return null;
    const restoredRole = sessionStorage.getItem(demoSessionKey);
    return restoredRole === 'medico' || restoredRole === 'secretaria'
      ? demoUser(restoredRole)
      : null;
  });
  const [loading, setLoading] = useState(!demoMode);
  const [sessionError, setSessionError] = useState('');

  useEffect(() => {
    if (demoMode) {
      return undefined;
    }

    const { auth } = getFirebaseServices();
    return onAuthStateChanged(auth, (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setLoading(false);
        return;
      }
      void (async () => {
        try {
          const token = await firebaseUser.getIdTokenResult();
          const role = token.claims.role;
          const clinicId = token.claims.clinicId;
          const active = token.claims.active;
          const authzVersion = token.claims.authzVersion;
          if (
            (role !== 'medico' && role !== 'secretaria') ||
            typeof clinicId !== 'string' ||
            active !== true ||
            typeof authzVersion !== 'number'
          ) {
            setSessionError(
              'Seu perfil de acesso está incompleto. Fale com a administração da clínica.',
            );
            setUser(null);
            return;
          }
          setSessionError('');
          setUser({
            id: firebaseUser.uid,
            clinicId,
            displayName: firebaseUser.displayName ?? 'Profissional do consultório',
            email: firebaseUser.email ?? '',
            role,
            avatarColor:
              role === 'medico' ? tokens.color.doctorAvatar : tokens.color.secretaryAvatar,
          });
        } catch (error) {
          setSessionError(
            toPortugueseError(error, 'Não foi possível restaurar sua sessão. Entre novamente.')
              .message,
          );
          setUser(null);
        } finally {
          setLoading(false);
        }
      })();
    });
  }, [demoMode]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      demoMode,
      sessionError,
      async signIn(email, password, preferredRole) {
        if (demoMode) {
          if (!email.trim() || !password) throw new Error('Informe e-mail e senha.');
          const role =
            preferredRole ??
            (email.toLocaleLowerCase().includes('secret') ? 'secretaria' : 'medico');
          sessionStorage.setItem(demoSessionKey, role);
          setUser(demoUser(role));
          return;
        }
        const { auth } = getFirebaseServices();
        try {
          await signInWithEmailAndPassword(auth, email, password);
        } catch (error) {
          throw toPortugueseError(
            error,
            'Não foi possível entrar. Confira seus dados e tente novamente.',
          );
        }
      },
      async signOut() {
        if (demoMode) {
          sessionStorage.removeItem(demoSessionKey);
          setUser(null);
          return;
        }
        await firebaseSignOut(getFirebaseServices().auth);
      },
    }),
    [demoMode, loading, sessionError, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider.');
  return context;
}
